import { useRef } from "react";

import { observer } from "mobx-react-lite";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { AlertDialog } from "@/components/dialog/alert/AlertDialog";
import { ControlsRow } from "@/components/doc/left-sidebar/ControlsRow";
import {
  CONTROLS_ROW_BUTTON_CLASS_NAME,
  CONTROLS_ROW_BUTTON_WRAPPER_CLASS_NAME,
  CONTROLS_ROW_CLASS_NAME,
} from "@/components/doc/left-sidebar/controlsRowStyles";
import { SearchButton } from "@/components/doc/left-sidebar/top-menu/SearchButton";
import { useHintHover } from "@/components/hints/useHintHover";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { useStore } from "@/store";
import { docPageUrl } from "@/urls";

import { CreateDocButton } from "./CreateDocButton";
import { ACCEPTED_IMPORT_FILE_TYPES_STRING } from "./FileDndContext";
import { useCreateInTree } from "./useCreateInTree";

import type { DropdownItem } from "@/components/sidebar/Dropdown";

const PDF_IMPORT_FILE_TYPES = ".pdf";
const LATEX_IMPORT_FILE_TYPES = ".tex,.zip,.gz";
const MARKDOWN_IMPORT_FILE_TYPES = ".md";
const NOTEBOOK_IMPORT_FILE_TYPES = ".ipynb";

export const DocumentTreeControls = observer(function DocumentTreeControls() {
  const t = useTranslations("documentsSidebar");
  const router = useRouter();
  const { classroom, documentTree, dialog } = useStore();
  const newFolderHintHandlers = useHintHover("newFolder");
  const importFileHintHandlers = useHintHover("import");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const { createFolderInTree } = useCreateInTree("top_panel");

  const createFolder = async () => {
    const target = documentTree.getTargetFolderAndIndex();
    if (!target) return;

    await createFolderInTree(target.folder, target.index);
  };

  const importFile = (fileTypes: string) => {
    if (!fileInputRef.current) return;

    fileInputRef.current.accept = fileTypes;
    fileInputRef.current.click();
  };

  const importFromOverleaf = () => {
    dialog.importFromOverleafDialogStore.open();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);

    // Reset so the same files can be selected again
    e.target.value = "";

    const target = documentTree.getTargetFolderAndIndex();
    if (files.length === 0 || !target) return;

    const firstDocId = await documentTree.enqueueFilesForUpload(
      files,
      target.folder.uid,
      target.index,
    );
    if (firstDocId) {
      router.push(docPageUrl({ docId: firstDocId }));
    }
  };

  if (!documentTree.root) {
    return null;
  }

  const isStudent = classroom.isStudentIn(documentTree.team);

  const importFromNotion = () => {
    dialog.importFromNotionDialog.open();
  };

  const importItems: DropdownItem[] = [
    {
      type: "title",
      title: t("importFrom"),
    },
    {
      title: t("importPdfFile"),
      icon: <Icon name="FilePdf" className="sm:w-[28px] sm:h-[28px]" />,
      onClick: () => importFile(PDF_IMPORT_FILE_TYPES),
    },
    {
      title: t("importLatex"),
      caption: t("importLatexDescription"),
      icon: <Icon name="FileTex" className="sm:w-[28px] sm:h-[28px]" />,
      onClick: () => importFile(LATEX_IMPORT_FILE_TYPES),
    },
    {
      title: t("importMarkdownFile"),
      icon: <Icon name="File" className="sm:w-[28px] sm:h-[28px]" />,
      onClick: () => importFile(MARKDOWN_IMPORT_FILE_TYPES),
    },
    {
      title: t("importNotebook"),
      icon: <Icon name="Code" className="sm:w-[28px] sm:h-[28px]" />,
      onClick: () => importFile(NOTEBOOK_IMPORT_FILE_TYPES),
    },
    {
      type: "separator",
    },
    {
      title: t("importFromNotion"),
      icon: <Icon name="Notion" className="sm:w-[28px] sm:h-[28px]" />,
      onClick: importFromNotion,
    },
    {
      title: t("importFromOverleaf"),
      icon: <Icon name="Overleaf" className="sm:w-[28px] sm:h-[28px]" />,
      onClick: importFromOverleaf,
    },
  ];

  // TODO: disable for trash folder
  return (
    <ControlsRow
      className={CONTROLS_ROW_CLASS_NAME}
      contentClassName="justify-between"
      data-testid="document-tree-controls"
      toolbarLabel={t("documentActions")}
      searchButtonRef={searchButtonRef}
    >
      {!isStudent && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMPORT_FILE_TYPES_STRING}
            multiple
            className="hidden"
            onChange={handleFileSelected}
          />
          <CreateDocButton hasText />
          <Sidebar.Button
            {...newFolderHintHandlers}
            onClick={createFolder}
            icon={<Icon name="AddFolder" className="sm:w-[28px] sm:h-[28px]" />}
            title={t("newFolder")}
            className={CONTROLS_ROW_BUTTON_CLASS_NAME}
            wrapperClassName={CONTROLS_ROW_BUTTON_WRAPPER_CLASS_NAME}
          />
          <Dropdown
            toggleButtonComponent={
              <Sidebar.Button
                {...importFileHintHandlers}
                as="div"
                icon={<Icon name="Import3" className="sm:w-[28px] sm:h-[28px]" />}
                title={t("import")}
                className={CONTROLS_ROW_BUTTON_CLASS_NAME}
                wrapperClassName={CONTROLS_ROW_BUTTON_WRAPPER_CLASS_NAME}
              />
            }
            items={importItems}
          />
        </>
      )}
      <SearchButton
        ref={searchButtonRef}
        className={CONTROLS_ROW_BUTTON_CLASS_NAME}
        wrapperClassName={CONTROLS_ROW_BUTTON_WRAPPER_CLASS_NAME}
      />
      <AlertDialog
        isOpen={documentTree.uploadError !== undefined}
        title={t("uploadError")}
        description={documentTree.uploadError}
        onClose={() => documentTree.dismissUploadError()}
      />
    </ControlsRow>
  );
});
